
import { isLeapYear, getDaysInMonth } from './utils.js'
import { MILLION, THOUSAND, ZERO, SIXTY, NEGATIVE_ONE } from './constants.js'

export function balanceISOYearMonth (yearParam, monthParam) {
  let year = yearParam
  let month = monthParam

  if (!Number.isFinite(year) || !Number.isFinite(month)) {
    throw new RangeError('infinity is out of range')
  }

  month -= 1
  year += Math.floor(month / 12)
  month %= 12
  if (month < 0) month += 12
  month += 1
  return { year, month }
}

export function balanceISODate (yearParam, monthParam, dayParam) {
  let year = yearParam
  let month = monthParam
  let day = dayParam

  if (!Number.isFinite(day)) {
    throw new RangeError('infinity is out of range')
  }

  ;({ year, month } = balanceISOYearMonth(year, month))
  let daysInYear = 0
  let testYear = month > 2 ? year : year - 1

  while (((daysInYear = isLeapYear(testYear) ? 366 : 365), day < -daysInYear)) {
    year -= 1
    testYear -= 1
    day += daysInYear
  }

  testYear += 1

  while (((daysInYear = isLeapYear(testYear) ? 366 : 365), day > daysInYear)) {
    year += 1
    testYear += 1
    day -= daysInYear
  }

  while (day < 1) {
    ;({ year, month } = balanceISOYearMonth(year, month - 1))
    day += getDaysInMonth(year, month)
  }

  while (day > getDaysInMonth(year, month)) {
    day -= getDaysInMonth(year, month)
    ;({ year, month } = balanceISOYearMonth(year, month + 1))
  }

  return { year, month, day }
}

export function balanceISODateTime (
  yearParam,
  monthParam,
  dayParam,
  hourParam,
  minuteParam,
  secondParam,
  millisecondParam,
  microsecondParam,
  nanosecondParam
) {
  const {
    deltaDays,
    hour,
    minute,
    second,
    millisecond,
    microsecond,
    nanosecond
  } = balanceTime(
    hourParam,
    minuteParam,
    secondParam,
    millisecondParam,
    microsecondParam,
    nanosecondParam
  )
  const { year, month, day } = balanceISODate(
    yearParam,
    monthParam,
    dayParam + deltaDays
  )
  return {
    year,
    month,
    day,
    hour,
    minute,
    second,
    millisecond,
    microsecond,
    nanosecond
  }
}

export function balanceTime (
  hour,
  minute,
  second,
  millisecond,
  microsecond,
  nanosecond
) {
  if (
    !Number.isFinite(hour) ||
    !Number.isFinite(minute) ||
    !Number.isFinite(second) ||
    !Number.isFinite(millisecond) ||
    !Number.isFinite(microsecond) ||
    !Number.isFinite(nanosecond)
  ) {
    console.log(
      hour,
      minute,
      second,
      millisecond,
      microsecond,
      nanosecond)
    throw new RangeError('infinity is out of range')
  }

  microsecond += Math.floor(nanosecond / 1000)
  nanosecond = nonNegativeModulo(nanosecond, 1000)

  millisecond += Math.floor(microsecond / 1000)
  microsecond = nonNegativeModulo(microsecond, 1000)

  second += Math.floor(millisecond / 1000)
  millisecond = nonNegativeModulo(millisecond, 1000)

  minute += Math.floor(second / 60)
  second = nonNegativeModulo(second, 60)

  hour += Math.floor(minute / 60)
  minute = nonNegativeModulo(minute, 60)

  const deltaDays = Math.floor(hour / 24)
  hour = nonNegativeModulo(hour, 24)

  return {
    deltaDays,
    hour,
    minute,
    second,
    millisecond,
    microsecond,
    nanosecond
  }
}

function nonNegativeModulo (x, y) {
  let result = x % y
  if (Object.is(result, -0)) return 0
  if (result < 0) result += y
  return result
}

export function balanceDuration (
  daysParam,
  hoursParam,
  minutesParam,
  secondsParam,
  millisecondsParam,
  microsecondsParam,
  nanosecondsParam,
  largestUnit,
  relativeTo = undefined
) {
  let result = balancePossiblyInfiniteDuration(
    daysParam,
    hoursParam,
    minutesParam,
    secondsParam,
    millisecondsParam,
    microsecondsParam,
    nanosecondsParam,
    largestUnit,
    relativeTo
  );
  if (result === 'positive overflow' || result === 'negative overflow') {
    throw new RangeError('Duration out of range');
  } else {
    return result;
  }
}
function balancePossiblyInfiniteDuration (
  daysParam,
  hoursParam,
  minutesParam,
  secondsParam,
  millisecondsParam,
  microsecondsParam,
  nanosecondsParam,
  largestUnit,
  relativeTo = undefined
) {
  let days = daysParam;
  let nanosecondsBigInt,
    microsecondsBigInt,
    millisecondsBigInt,
    secondsBigInt,
    minutesBigInt,
    hoursBigInt

  nanosecondsBigInt = totalDurationNanoseconds(
    days,
    hoursParam,
    minutesParam,
    secondsParam,
    millisecondsParam,
    microsecondsParam,
    nanosecondsParam,
    0
  )
  if (largestUnit === 'year' || largestUnit === 'month' || largestUnit === 'week' || largestUnit === 'day') {
    ({ days, nanoseconds: nanosecondsBigInt } = nanosecondsToDays(nanosecondsBigInt, relativeTo));
  } else {
    days = 0
  }

  const sign = (nanosecondsBigInt < ZERO) ? -1 : 1;
  nanosecondsBigInt = abs(nanosecondsBigInt);
  microsecondsBigInt = millisecondsBigInt = secondsBigInt = minutesBigInt = hoursBigInt = ZERO;

  switch (largestUnit) {
    case 'year':
    case 'month':
    case 'week':
    case 'day':
    case 'hour':
      microsecondsBigInt = nanosecondsBigInt / THOUSAND
      nanosecondsBigInt %= THOUSAND
      millisecondsBigInt = microsecondsBigInt / THOUSAND
      microsecondsBigInt %= THOUSAND
      secondsBigInt = millisecondsBigInt / THOUSAND
      millisecondsBigInt %= THOUSAND
      minutesBigInt = secondsBigInt / SIXTY
      secondsBigInt %= SIXTY
      hoursBigInt = minutesBigInt / SIXTY
      minutesBigInt %= SIXTY
      break
    case 'minute':
      microsecondsBigInt = nanosecondsBigInt / THOUSAND
      nanosecondsBigInt %= THOUSAND
      millisecondsBigInt = microsecondsBigInt / THOUSAND
      microsecondsBigInt %= THOUSAND
      secondsBigInt = millisecondsBigInt / THOUSAND
      millisecondsBigInt %= THOUSAND
      minutesBigInt = secondsBigInt / SIXTY
      secondsBigInt %= SIXTY
      break
    case 'second':
      microsecondsBigInt = nanosecondsBigInt / THOUSAND
      nanosecondsBigInt %= THOUSAND
      millisecondsBigInt = microsecondsBigInt / THOUSAND
      microsecondsBigInt %= THOUSAND
      secondsBigInt = millisecondsBigInt / THOUSAND
      millisecondsBigInt %= THOUSAND
      break
    case 'millisecond':
      microsecondsBigInt = nanosecondsBigInt / THOUSAND
      nanosecondsBigInt %= THOUSAND
      millisecondsBigInt = microsecondsBigInt / THOUSAND
      microsecondsBigInt %= THOUSAND
      break
    case 'microsecond':
      microsecondsBigInt = nanosecondsBigInt / THOUSAND
      nanosecondsBigInt %= THOUSAND
      break
    case 'nanosecond':
      break
    default:
      throw new Error('assert not reached');
  }

  const hours = Number(hoursBigInt) * sign;
  const minutes = Number(minutesBigInt) * sign;
  const seconds = Number(secondsBigInt) * sign;
  const milliseconds = Number(millisecondsBigInt) * sign;
  const microseconds = Number(microsecondsBigInt) * sign;
  const nanoseconds = Number(nanosecondsBigInt) * sign;

  for (const prop of [days, hours, minutes, seconds, milliseconds, microseconds, nanoseconds]) {
    if (!Number.isFinite(prop)) {
      if (sign === 1) {
        return 'positive overflow'
      } else {
        return 'negative overflow'
      }
    }
  }

  return { days, hours, minutes, seconds, milliseconds, microseconds, nanoseconds };
}

export function totalDurationNanoseconds (
  daysParam,
  hoursParam,
  minutesParam,
  secondsParam,
  millisecondsParam,
  microsecondsParam,
  nanosecondsParam,
  offsetShift
) {
  const days = BigInt(daysParam)
  let nanoseconds = BigInt(nanosecondsParam)
  if (daysParam !== 0) nanoseconds = (BigInt(nanosecondsParam) - BigInt(offsetShift))
  const hours = BigInt(hoursParam) + (days * BigInt(24))
  const minutes = BigInt(minutesParam) + (hours * SIXTY)
  const seconds = BigInt(secondsParam) + (minutes * SIXTY)
  const milliseconds = BigInt(millisecondsParam) + (seconds * THOUSAND)
  const microseconds = BigInt(microsecondsParam) + (milliseconds * THOUSAND)
  return BigInt(nanoseconds) + (microseconds * THOUSAND)
}

function abs (x) {
  if (x < ZERO) return x * NEGATIVE_ONE
  return x
}

export function nanosecondsToDays (nanosecondsParam) {
  const sign = Math.sign(Number(nanosecondsParam))
  let nanoseconds = BigInt(nanosecondsParam)
  let dayLengthNs = 86400e9
  if (sign === 0) return { days: 0, nanoseconds: ZERO, dayLengthNs }
  const days = Number(nanoseconds / BigInt(dayLengthNs))
  nanoseconds = nanoseconds % BigInt(dayLengthNs)
  // ;({ quotient: days, remainder: nanoseconds } = divmod(nanoseconds, JSBI.BigInt(dayLengthNs)));
  return { days, nanoseconds, dayLengthNs }
  /*
  if (!IsTemporalZonedDateTime(relativeTo)) {
    let days = nanoseconds / BigInt(dayLengthNs)
    ;({ quotient: days, remainder: nanoseconds } = divmod(nanoseconds, JSBI.BigInt(dayLengthNs)));
    return { days: Number(days), nanoseconds, dayLengthNs };
  }

  const startNs = GetSlot(relativeTo, EPOCHNANOSECONDS);
  const start = GetSlot(relativeTo, INSTANT);
  const endNs = JSBI.add(startNs, nanoseconds);
  const end = new TemporalInstant(endNs);
  const timeZone = GetSlot(relativeTo, TIME_ZONE);
  const calendar = GetSlot(relativeTo, CALENDAR);

  // Find the difference in days only.
  const dtStart = BuiltinTimeZoneGetPlainDateTimeFor(timeZone, start, calendar);
  const dtEnd = BuiltinTimeZoneGetPlainDateTimeFor(timeZone, end, calendar);
  let { days } = DifferenceISODateTime(
    GetSlot(dtStart, ISO_YEAR),
    GetSlot(dtStart, ISO_MONTH),
    GetSlot(dtStart, ISO_DAY),
    GetSlot(dtStart, ISO_HOUR),
    GetSlot(dtStart, ISO_MINUTE),
    GetSlot(dtStart, ISO_SECOND),
    GetSlot(dtStart, ISO_MILLISECOND),
    GetSlot(dtStart, ISO_MICROSECOND),
    GetSlot(dtStart, ISO_NANOSECOND),
    GetSlot(dtEnd, ISO_YEAR),
    GetSlot(dtEnd, ISO_MONTH),
    GetSlot(dtEnd, ISO_DAY),
    GetSlot(dtEnd, ISO_HOUR),
    GetSlot(dtEnd, ISO_MINUTE),
    GetSlot(dtEnd, ISO_SECOND),
    GetSlot(dtEnd, ISO_MILLISECOND),
    GetSlot(dtEnd, ISO_MICROSECOND),
    GetSlot(dtEnd, ISO_NANOSECOND),
    calendar,
    'day',
    ObjectCreate(null)
  );
  let intermediateNs = AddZonedDateTime(start, timeZone, calendar, 0, 0, 0, days, 0, 0, 0, 0, 0, 0);
  // may disambiguate

  // If clock time after addition was in the middle of a skipped period, the
  // endpoint was disambiguated to a later clock time. So it's possible that
  // the resulting disambiguated result is later than endNs. If so, then back
  // up one day and try again. Repeat if necessary (some transitions are
  // > 24 hours) until either there's zero days left or the date duration is
  // back inside the period where it belongs. Note that this case only can
  // happen for positive durations because the only direction that
  // `disambiguation: 'compatible'` can change clock time is forwards.
  if (sign === 1) {
    while (days > 0 && JSBI.greaterThan(intermediateNs, endNs)) {
      --days;
      intermediateNs = AddZonedDateTime(start, timeZone, calendar, 0, 0, 0, days, 0, 0, 0, 0, 0, 0);
      // may do disambiguation
    }
  }
  nanoseconds = JSBI.subtract(endNs, intermediateNs);

  let isOverflow = false;
  let relativeInstant = new TemporalInstant(intermediateNs);
  do {
    // calculate length of the next day (day that contains the time remainder)
    const oneDayFartherNs = AddZonedDateTime(relativeInstant, timeZone, calendar, 0, 0, 0, sign, 0, 0, 0, 0, 0, 0);
    const relativeNs = GetSlot(relativeInstant, EPOCHNANOSECONDS);
    dayLengthNs = JSBI.toNumber(JSBI.subtract(oneDayFartherNs, relativeNs));
    isOverflow = (BigInt(sign) * (nanoseconds - BigInt(dayLengthNs))) > ZERO
    if (isOverflow) {
      nanoseconds = (nanoseconds - BigInt(dayLengthNs));
      relativeInstant = new TemporalInstant(oneDayFartherNs);
      days += sign;
    }
  } while (isOverflow);
  return { days, nanoseconds, dayLengthNs: Math.abs(dayLengthNs) };
  */
}
